import React, { useState } from 'react';
import { XMarkIcon, CreditCardIcon, ShieldCheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import VerificationProviderSelector from './VerificationProviderSelector';

interface VerificationPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: any;
}

const VerificationPurchaseModal: React.FC<VerificationPurchaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'provider' | 'payment' | 'processing' | 'success'>('provider');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');

  console.log(`💳 Verification purchase modal: user=${user?.email}, step=${step}`);

  if (!isOpen) return null;

  const handleProviderSelect = (provider: string, packageType: string, paymentMethod: string) => {
    console.log(`🎯 Provider selected: ${provider}, package: ${packageType}, payment: ${paymentMethod}`);
    setSelectedProvider(provider);
    setSelectedPackage(packageType);
    setSelectedPaymentMethod(paymentMethod);
    setStep('payment');
  };

  const handlePurchase = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      console.log(`💳 Starting verification purchase for ${user.email} with ${selectedProvider}:${selectedPackage}:${selectedPaymentMethod}`);
      
      const token = localStorage.getItem('token');
      
      // Choose endpoint based on payment method
      const endpoint = selectedPaymentMethod === 'direct' 
        ? 'http://localhost:5000/api/verification/initiate-direct'
        : 'http://localhost:5000/api/verification/purchase-intermediary';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: selectedProvider,
          package: selectedPackage,
          paymentMethodId: selectedPaymentMethod === 'intermediary' ? 'pm_card_visa' : undefined
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Payment failed');
      }

      if (selectedPaymentMethod === 'direct') {
        // Redirect to provider payment page
        if (data.paymentUrl) {
          window.open(data.paymentUrl, '_blank');
        }
        setStep('processing');
        
        // Simulate processing for demo
        setTimeout(() => {
          setStep('success');
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 3000);
        }, 2000);
        
      } else if (data.success) {
        setStep('processing');
        
        // Simulate processing time
        setTimeout(() => {
          setStep('success');
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 3000);
        }, 2000);
        
      } else if (data.clientSecret) {
        // Handle 3D Secure or other payment confirmation
        console.log('Payment requires confirmation:', data.clientSecret);
        setStep('success');
      }

    } catch (error: any) {
      console.error('Verification purchase error:', error);
      setError(error.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPaymentStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-blue-500" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          Complete Your Verification
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {selectedProvider && selectedPackage && (
            <>Selected: {selectedProvider} {selectedPackage} package via {selectedPaymentMethod} payment</>
          )}
        </p>
      </div>

      {/* Selected Options Summary */}
      {selectedProvider && selectedPackage && selectedPaymentMethod && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Your Selection:</h4>
          <div className="text-sm text-blue-700 space-y-1">
            <div>Provider: <span className="font-medium capitalize">{selectedProvider}</span></div>
            <div>Package: <span className="font-medium capitalize">{selectedPackage}</span></div>
            <div>Payment: <span className="font-medium capitalize">{selectedPaymentMethod}</span></div>
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">What's Included:</h4>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-center">
            <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
            Comprehensive background check
          </li>
          <li className="flex items-center">
            <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
            Blue verification badge on profile
          </li>
          <li className="flex items-center">
            <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
            Authorization for in-home lessons
          </li>
          <li className="flex items-center">
            <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
            Higher search ranking & visibility
          </li>
          <li className="flex items-center">
            <CheckBadgeIcon className="h-4 w-4 text-green-500 mr-2" />
            Instant booking eligibility
          </li>
        </ul>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ClockIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
          <div>
            <h5 className="font-medium text-blue-900">Processing Timeline</h5>
            <p className="text-sm text-blue-700 mt-1">
              After payment, you'll receive background check instructions via email. 
              Results typically arrive within 2-5 business days.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <span className="text-gray-900">Background Check Verification</span>
          <span className="text-lg font-semibold">$29.99</span>
        </div>
        <div className="text-xs text-gray-500 space-y-1">
          <div>• Valid for 1 year</div>
          <div>• Secure payment via Stripe</div>
          <div>• Refundable if background check fails</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          onClick={handlePurchase}
          disabled={isProcessing}
          className={`flex-1 flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
            isProcessing 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {isProcessing ? (
            <>
              <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              Processing...
            </>
          ) : (
            <>
              <CreditCardIcon className="h-4 w-4 mr-2" />
              Pay $29.99 & Get Verified
            </>
          )}
        </button>
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center space-y-6">
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900">
          Payment Successful!
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Initiating background check process...
        </p>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center space-y-6">
      <div className="flex items-center justify-center">
        <CheckBadgeIcon className="h-16 w-16 text-green-500" />
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900">
          🎉 Verification Started!
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Check your email for background check instructions. We'll notify you when verification is complete.
        </p>
      </div>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-medium text-green-900 mb-2">Next Steps:</h4>
        <ol className="text-sm text-green-700 space-y-1 text-left">
          <li>1. Check your email for background check link</li>
          <li>2. Complete the verification form (5-10 minutes)</li>
          <li>3. Wait for results (2-5 business days)</li>
          <li>4. Start teaching in-home lessons!</li>
        </ol>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={step === 'payment' ? onClose : undefined}></div>

        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Teacher Verification
            </h2>
            {(step === 'payment' || step === 'processing') && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {step === 'provider' && (
            <VerificationProviderSelector
              user={user}
              onProviderSelect={handleProviderSelect}
              onCancel={onClose}
            />
          )}
          {step === 'payment' && renderPaymentStep()}
          {step === 'processing' && renderProcessingStep()}
          {step === 'success' && renderSuccessStep()}
        </div>
      </div>
    </div>
  );
};

export default VerificationPurchaseModal; 